// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { Paper } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowIdGetter,
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
  GridEventListener,
  GridRowHeightParams,
} from '@mui/x-data-grid';
import { useLocalStorage } from '../../contexts/LocalStorageContext';
import { dataGridSx } from './dataGridSx';
import { withScrollableCell } from './withScrollableCell';

type DataTableProps<R extends GridValidRowModel> = {
  columns: GridColDef<R>[];
  rows: R[];
  rowKey: GridRowIdGetter<R>;
  toolbar?: React.FC<{ selected: R[] }>;
  onRowClick?: GridEventListener<'rowClick'> | undefined;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  defaultSortModel?: GridSortModel;
  disableVirtualization?: boolean;
  columnVisibilityModel?: Record<string, boolean>;
  hideFooter?: boolean;
  getRowHeight?: (params: GridRowHeightParams) => 'auto' | number;
};

export const DataTable = <R extends GridValidRowModel>({
  columns,
  rows,
  rowKey,
  toolbar,
  onFilterModelChange,
  defaultSortModel,
  onRowClick,
  disableVirtualization,
  columnVisibilityModel,
  hideFooter = false,
  getRowHeight,
}: DataTableProps<R>): JSX.Element => {
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel || []);
  const isDark = !!useLocalStorage()?.localStorageState?.isDark;
  // Use the withScrollableCell utility to wrap columns
  const scrollableColumns = withScrollableCell(columns);
  const dataGrid = (
    <DataGrid
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      disableVirtualization={!!disableVirtualization}
      sx={(theme) => dataGridSx(theme, !!onRowClick)}
      columns={scrollableColumns} // Use scrollable columns
      rows={rows}
      getRowId={rowKey}
      slots={{ toolbar }}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelection={false}
      columnVisibilityModel={columnVisibilityModel}
      hideFooter={hideFooter}
      getRowHeight={getRowHeight}
    />
  );
  if (isDark) {
    return <Paper sx={{ height: '100%' }}>{dataGrid}</Paper>;
  }
  return dataGrid;
};
